import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
    alpha,
    Box,
    Button,
    Card,
    CardActionArea,
    CardContent,
    Container,
    Paper,
    Stack,
    Typography,
} from "@mui/material";
import ArrowBackIosNewOutlinedIcon from "@mui/icons-material/ArrowBackIosNewOutlined";
import Groups2OutlinedIcon from "@mui/icons-material/Groups2Outlined";
import {
    getStaffRoleOptions,
    normalizeStaffRole,
    staffRoleNeedsHomeroomSetupFlow,
    staffRoleNeedsSubjectFlow,
} from "../../../utils/staffRoles";

const ChooseStaffRole = () => {
    const navigate = useNavigate();
    const { currentUser } = useSelector((state) => state.user);
    const staffRoleOptions = getStaffRoleOptions(currentUser?.schoolType);
    const [selected, setSelected] = useState(staffRoleOptions[0].value);

    const continueHandler = () => {
        const role = normalizeStaffRole(selected);
        if (staffRoleNeedsHomeroomSetupFlow(role) || staffRoleNeedsSubjectFlow(role)) {
            navigate("/Admin/teachers/chooseclass", { state: { staffRole: role } });
            return;
        }
        navigate("/Admin/staff/add", { state: { staffRole: role } });
    };

    return (
        <Box
            sx={{
                minHeight: "100%",
                py: { xs: 2, sm: 3 },
                background: (t) =>
                    t.palette.mode === "light"
                        ? `linear-gradient(180deg, ${alpha(t.palette.primary.main, 0.05)} 0%, ${t.palette.grey[50]} 40%)`
                        : alpha(t.palette.common.black, 0.15),
            }}
        >
            <Container maxWidth="md">
                <Button
                    startIcon={<ArrowBackIosNewOutlinedIcon />}
                    onClick={() => navigate("/Admin/teachers")}
                    sx={{ mb: 2 }}
                >
                    Back to staff
                </Button>
                <Paper elevation={0} sx={{ p: { xs: 2.5, sm: 3.5 }, borderRadius: 3 }}>
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                        <Groups2OutlinedIcon color="primary" fontSize="large" />
                        <Box>
                            <Typography variant="h5" fontWeight={700}>
                                Add staff member
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Choose the role for {currentUser?.schoolName || "your school"}. You will create temporary
                                login credentials; the staff member completes their profile after first sign-in.
                            </Typography>
                        </Box>
                    </Stack>

                    <Stack spacing={1.5}>
                        {staffRoleOptions.map((opt) => {
                            const active = selected === opt.value;
                            return (
                                <Card
                                    key={opt.value}
                                    variant="outlined"
                                    sx={{
                                        borderColor: active ? "primary.main" : "divider",
                                        bgcolor: active ? (t) => alpha(t.palette.primary.main, 0.06) : "background.paper",
                                    }}
                                >
                                    <CardActionArea onClick={() => setSelected(opt.value)}>
                                        <CardContent>
                                            <Typography fontWeight={700}>{opt.label}</Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {opt.description}
                                            </Typography>
                                        </CardContent>
                                    </CardActionArea>
                                </Card>
                            );
                        })}
                    </Stack>

                    <Stack direction="row" justifyContent="flex-end" spacing={2} sx={{ mt: 3 }}>
                        <Button variant="outlined" onClick={() => navigate("/Admin/teachers")}>
                            Cancel
                        </Button>
                        <Button variant="contained" size="large" onClick={continueHandler}>
                            Continue
                        </Button>
                    </Stack>
                </Paper>
            </Container>
        </Box>
    );
};

export default ChooseStaffRole;
